import React, {useEffect, useState} from "react";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	Typography,
	Chip,
	Toolbar,
	Button,
	CircularProgress,
	Box,
	IconButton
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import API from "../../../network/dashboardAPI";
import DelipointSettingDialog from "./component/DelipointSettingDialog";
import ActionButtons from "./component/DeleteDialog";
import useAlert from "../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc",
		minHeight: "100vh"
	},
	paper: {
		borderRadius: 12,
		overflow: "hidden",
		boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
	},
	toolbar: {
		display: "flex",
		justifyContent: "space-between",
		padding: theme.spacing(2)
	},
	searchBox: {
		display: "flex",
		alignItems: "center",
		padding: "2px 8px",
		borderRadius: 8,
		backgroundColor: "#fff",
		border: "1px solid #ddd",
		width: 300
	},
	searchInput: {
		marginLeft: theme.spacing(1),
		flex: 1
	},
	tableHead: {
		backgroundColor: "#f4f6f8",
		display: 'flex',
		fontWeight: 600,
		borderBottom: '1px solid #eee',
		padding: '8px 0',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		borderBottom: '1px solid #f0f0f0',
		transition: 'background 0.2s',
		minHeight: 48,
	},
	cell: {
		flex: 1,
		padding: '8px',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	dragHandle: {
		cursor: 'grab',
		marginRight: 8,
		userSelect: 'none',
	},
	statusChip: {
		fontWeight: "bold"
	},
	actionButton: {
		marginLeft: theme.spacing(1)
	},
	loadingBox: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(3)
	},
	refreshButton: {
		marginLeft: theme.spacing(1)
	},
	actionSection: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1)
	}
}));

export default function DelipointSettingsList() {
	const classes = useStyles();
	const alert = useAlert();

	const [data, setData] = useState([]);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [orderBy, setOrderBy] = useState("id");
	const [order, setOrder] = useState("asc");
	const [loading, setLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(null);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);

	const getRowId = (row) => row?._id || row?.setting_key || row?.id;

	const fetchDelipoints = async () => {
		try {
			setLoading(true);
			const params = {
				search,
				page,
				limit: rowsPerPage,
				sortField: orderBy,
				sortOrder: order,
				_locale: "en"
			};

			const response = await API.getDelipointSettingsList(params);
			const res = await response.json();

			if (res.success) {
				setData(res.data || []);
				setTotal(res.total || 0);
				if ((res.total || 0) === 0 && search) {
					alert.toast("No delipoint found for your search", "info");
				}
			} else {
				alert.error("Failed to Load", res.message || "Unable to fetch delipoint tiers");
			}
		} catch (err) {
			console.error("Error fetching delipoint:", err);
			alert.error(
				"Network Error",
				"Failed to load delipoint. Please check your connection."
			);
		} finally {
			setLoading(false);
		}
	};

	const handleSort = (field) => {
		if (orderBy === field) {
			setOrder(order === "asc" ? "desc" : "asc");
		} else {
			setOrderBy(field);
			setOrder("asc");
		}
		setPage(0);
	};

	const handleSearch = (e) => {
		setSearch(e.target.value);
		setPage(0);
	};

	const handleSearchSubmit = (e) => {
		if (e.key === "Enter") {
			fetchDelipoints();
		}
	};

	const handleEdit = (item) => {
		// Ensure the item always has an id property for dialog usage
		const id = getRowId(item);
		const itemWithId = id && !item.id ? { ...item, id } : item;
		setSelectedItem(itemWithId);
		setDialogOpen(true);
	};

	// Refresh list after dialog save
	const handleDialogSaved = (action, itemName) => {
		fetchDelipoints();
		if (action === "edit") {
			alert.success("Delipoint Updated", `"${itemName}" has been updated successfully.`);
			alert.toast("Delipoint updated");
		}
	};

	// Debounced search effect
	useEffect(() => {
		const timer = setTimeout(() => {
			fetchDelipoints();
		}, 500); // 500ms debounce

		return () => clearTimeout(timer);
	}, [search, page, rowsPerPage, orderBy, order]);

	// Initial load
	useEffect(() => {
		fetchDelipoints();
	}, []);

	return (
		<div className={classes.root}>
			<Typography variant="h5" gutterBottom style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
				Delipoint Settings
				{loading && <CircularProgress size={20}/>} 
			</Typography>

			<Paper className={classes.paper}>
				<Toolbar className={classes.toolbar}>
					<div className={classes.searchBox}>
						<SearchIcon color="action"/>
						<TextField
							placeholder="Search delipoint..."
							value={search}
							onChange={handleSearch}
							onKeyPress={handleSearchSubmit}
							className={classes.searchInput}
							InputProps={{disableUnderline: true}}
						/>
					</div>
				</Toolbar>

				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>ID</TableCell>
								<TableCell>Key</TableCell>
								<TableCell>Value</TableCell>
								<TableCell>Description</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading && data.length === 0 ? (
								<TableRow>
									<TableCell colSpan={5} align="center">
										<Box className={classes.loadingBox}>
											<CircularProgress/>
											<Typography variant="body2" style={{marginLeft: 16}}>
												Loading delipoint...
											</Typography>
										</Box>
									</TableCell>
								</TableRow>
							) : data.length === 0 ? (
								<TableRow>
									<TableCell colSpan={5} align="center" style={{ padding: 40 }}>
										<Typography variant="h6" color="textSecondary" gutterBottom>
											No delipoint found
										</Typography>
										{search && (
											<Typography variant="body2" color="textSecondary">
												Try adjusting your search terms
											</Typography>
										)}
									</TableCell>
								</TableRow>
							) : (
								data.map((row) => (
									<TableRow key={getRowId(row)}>
										<TableCell>{row._id}</TableCell>
										<TableCell>{row.setting_key}</TableCell>
										<TableCell>
											<TextField
												value={row.setting_value}
												onChange={e => {
													const newValue = e.target.value;
													setData(prev => prev.map(r => getRowId(r) === getRowId(row) ? { ...r, setting_value: newValue } : r));
												}}
												onBlur={async (e) => {
													const newValue = e.target.value;
													try {
														setLoading(true);
														const payload = {
															id: row._id,
															setting_key: row.setting_key,
															setting_value: newValue,
															description: row.description || null
														};
														const response = await API.updateDelipointSetting(payload);
														const res = await response.json();
														if (response.ok && res.success) {
															fetchDelipoints();
															alert.success('Updated', 'Value updated successfully');
														} else {
															alert.error('Update Failed', res.message || 'Could not update value');
														}
													} catch (err) {
														alert.error('Update Failed', err?.message || 'Could not update value');
													} finally {
														setLoading(false);
													}
												}}
												size="small"
												variant="outlined"
												fullWidth
											/>
										</TableCell>
										<TableCell>{row.description || '-'}</TableCell>
										<TableCell align="right">
											<ActionButtons
												row={row}
												handleEdit={handleEdit}
											/>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={total}
					page={page}
					onPageChange={(e, newPage) => setPage(newPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={e => {
						setRowsPerPage(parseInt(e.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</Paper>

			{/* Edit Dialog */}
			<DelipointSettingDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				onSaved={handleDialogSaved}
				initialData={selectedItem}
			/>
		</div>
	);
}